import { useState, useEffect } from "react";
import {
  Config,
  configs,
  Default,
  fromUrlParam,
  getConfig,
  toUrlParam,
} from "./prayertimes/config";
import { Text } from "./LocalizedText";
import countries from "i18n-iso-countries";
import deLocale from "i18n-iso-countries/langs/de.json";
import { SystemUtils } from "./SystemControl";
import { cachedFetch } from "./cachedFetch";

const Configurator = () => {
  countries.registerLocale(deLocale);

  const [config, _setConfig] = useState({ ...getConfig(), counter: 0 });
  const [cities, setCities] = useState<string[]>([]);

  const setConfig = (newConfig: Config) => {
    _setConfig({ ...newConfig, counter: config.counter + 1 });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "INIT_DATA") {
        const newConfig = event.data.data.config;
        if (typeof newConfig === "string") {
          _setConfig({
            ...fromUrlParam(
              newConfig.substring(newConfig.lastIndexOf("?") + 1),
            ),
            counter: 0,
          });
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // send back config to pi-signage
  useEffect(() => {
    if (config.counter > 0) {
      window.parent.postMessage(
        {
          type: "SAVE_DATA",
          key: "config",
          value: "http://localhost:8000" + toUrlParam(config).replace("city=%2Fapi%2F", "city=https%3A%2F%2Fopt.mk38.de%2F"),
        },
        "*",
      );
      window.parent.postMessage(
        {
          type: "SAVE_DATA",
          key: "rclone",
          value: `
[remote]
type = http
url = https://metinkale38.github.io/mosque-signage/
index_file = rclone.php
#rclone-opts=--files-from-raw <(curl -s --connect-timeout 5 --max-time 10 https://metinkale38.github.io/mosque-signage/rclone.txt | awk '{print $2}') --size-only --no-traverse`,
        },
        "*",
      );
    }
  }, [config]);

  const [remoteCities, setRemoteCities] = useState<
    { path: string; options: string[]; selected?: string }[]
  >([{ path: "/api", options: [] }]);

  const fetchCities = async (path: string, levelIndex: number) => {
    const data = JSON.parse(await cachedFetch(path));

    if (Array.isArray(data)) {
      setRemoteCities((prev) => {
        const newLevels = prev.slice(0, levelIndex + 1);
        newLevels[levelIndex] = { path, options: data, selected: undefined };
        return newLevels;
      });
    } else {
      setConfig({ ...config, city: path + "/csv" });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch("hash.php");
      const lines = (await response.text())
        .split("\n")
        .map((txt) => "." + txt.split("=")[0])
        .filter((file) => file.includes(".csv"));
      setCities(lines);
    };

    fetchData();
  }, []);

  useEffect(
    () => {
      fetchCities("/api", 0);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      /*Do not add fetchCities*/
    ],
  );

  useEffect(() => {
    SystemUtils.setConfig(toUrlParam(config).substring(1));
  }, [config]);

  const handleSelectChange = (levelIndex: number, value: string) => {
    setRemoteCities((prev) => {
      const newLevels = prev.slice(0, levelIndex + 1);
      newLevels[levelIndex] = { ...newLevels[levelIndex], selected: value };
      return newLevels;
    });

    const newPath = `${remoteCities[levelIndex].path}/${value}`;
    fetchCities(newPath, levelIndex + 1);
  };

  let tabIndex = 1;

  return (
    <div className="m-8 mx-auto flex w-max flex-col gap-2 rounded-lg bg-white p-6 shadow-md">
      <h2 className="mb-4 text-xl font-bold">Konfigurator</h2>

      {SystemUtils.getFlavor() === "playstore" ? (
        <></>
      ) : (
        <>
          <label className="mb-1 block">Vorlage</label>
          <select
            name="config"
            value={config.config}
            tabIndex={tabIndex++}
            onChange={(e) =>
              setConfig({
                ...(configs.find((c) => c.config === e.target.value) ||
                  Default),
              })
            }
            className="mb-2 w-full rounded border p-2"
          >
            <option value={undefined}>Default</option>
            {configs.map((config) => (
              <option key={config.config} value={config.config}>
                {config.config}
              </option>
            ))}
          </select>
        </>
      )}

      <label className="mb-1 block">Stadt</label>
      <input readOnly={true} value={config.city} />

      {remoteCities.map((level, index) => (
        <select
          tabIndex={tabIndex++}
          key={index}
          className="mb-2 w-full rounded border p-2"
          onChange={(e) => handleSelectChange(index, e.target.value)}
        >
          <option key={undefined} value={undefined}>
            {SystemUtils.getFlavor() === "playstore"
              ? "Stadt wählen"
              : "Andere wählen"}
          </option>
          {level.options
            .filter((c) => c !== config.city && c !== "Calc" && c !== "CSV")
            .map((city) => (
              <option key={city} value={city}>
                {index === 1 ? countries.getName(city, "de") : city}
              </option>
            ))}
        </select>
      ))}

      <label className="mb-1 block">Sprachen</label>
      <div className="mt-2 flex flex-col space-y-2">
        {Object.keys(Text.PrayerTimes.FAJR).map((lang, idx) => (
          <label key={idx} className="flex items-center space-x-2">
            <input
              type="checkbox"
              name={lang}
              checked={config.languages.includes(lang)}
              onChange={(e) =>
                setConfig({
                  ...config,
                  languages: e.target.checked
                    ? [...config.languages, lang]
                    : config.languages.filter((l) => l !== lang),
                })
              }
              tabIndex={tabIndex++}
            />
            <span>{lang.toUpperCase()}</span>
          </label>
        ))}
      </div>

      <label className="mb-1 block">Hintergrundfarbe</label>
      <input
        name="bgColor"
        value={config.bgColor}
        onChange={(e) => setConfig({ ...config, bgColor: e.target.value })}
        className="mb-2 w-full rounded border p-2"
        type="color"
        tabIndex={tabIndex++}
      />

      <label className="mb-1 block">Stil</label>
      <select
        name="style"
        value={config.style}
        onChange={(e) => setConfig({ ...config, style: e.target.value })}
        className="mb-2 w-full rounded border p-2"
        tabIndex={tabIndex++}
      >
        <option value="primary">Primary</option>
        <option value="secondary">Secondary</option>
      </select>

      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={config.showHighlight}
          onChange={(e) =>
            setConfig({ ...config, showHighlight: e.target.checked })
          }
          tabIndex={tabIndex++}
        />
        <span>Hervorhebung während der Gebetszeit</span>
      </label>

      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={config.showHighlightAlways}
          onChange={(e) =>
            setConfig({ ...config, showHighlightAlways: e.target.checked })
          }
          tabIndex={tabIndex++}
        />
        <span>Hebe Gebetszeit immer vor</span>
      </label>

      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={config.showSpecialDays}
          onChange={(e) =>
            setConfig({ ...config, showSpecialDays: e.target.checked })
          }
          tabIndex={tabIndex++}
        />
        <span>Zeige Religiöse Tage</span>
      </label>

      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={config.screenOnOff}
          onChange={(e) =>
            setConfig({ ...config, screenOnOff: e.target.checked })
          }
          tabIndex={tabIndex++}
        />
        <span>Automatisches Ein-/Ausschalten</span>
      </label>

      <label className="mb-1 block">
        Abweichendes Freitagsgebet zur Sommerzeit (z.B. 14:30)
      </label>
      <input
        type="text"
        name="cumaSummer"
        value={config.cumaSummer || ""}
        onChange={(e) => setConfig({ ...config, cumaSummer: e.target.value })}
        className="mb-2 w-full rounded border p-2"
        placeholder="HH:MM"
        tabIndex={tabIndex++}
      />

      <label className="mb-1 block">
        Abweichendes Freitagsgebet zur Winterzeit (z.B. 13:00)
      </label>
      <input
        type="text"
        name="cumaWinter"
        value={config.cumaWinter || ""}
        onChange={(e) => setConfig({ ...config, cumaWinter: e.target.value })}
        className="mb-2 w-full rounded border p-2"
        placeholder="HH:MM"
        tabIndex={tabIndex++}
      />

      <label className="mb-1 block">
        Morgengebet (-30: Halbe Stunde vor Sonnenaufgang, 15: 15min nach Fajr)
      </label>
      <input
        type="text"
        name="sabah"
        value={config.sabah || ""}
        onChange={(e) =>
          setConfig({ ...config, sabah: parseInt(e.target.value) })
        }
        className="mb-2 w-full rounded border p-2"
        placeholder="Zahl eingeben"
        tabIndex={tabIndex++}
      />

      <label className="mb-1 block">
        Morgengebet Ramadan (-30: Halbe Stunde vor Sonnenaufgang, 15: 15min nach
        Fajr)
      </label>
      <input
        type="text"
        name="sabahRamadan"
        value={config.sabahRamadan || ""}
        onChange={(e) =>
          setConfig({ ...config, sabahRamadan: parseInt(e.target.value) })
        }
        className="mb-2 w-full rounded border p-2"
        placeholder="Zahl eingeben"
        tabIndex={tabIndex++}
      />

      <div className="mt-4">
        <span className="text-sm text-gray-500">Generierte URL:</span>
        <a
          target="_blank"
          rel="noreferrer"
          href={window.location.href.split("?")[0] + toUrlParam(config)}
        >
          <p className="break-all text-blue-600">
            {window.location.href.split("?")[0] + toUrlParam(config)}
          </p>
        </a>
      </div>
    </div>
  );
};

export default Configurator;
